/**
 * Admin API Client
 */

// Use environment variable or default to relative path for same-origin deployment
const API_BASE = import.meta.env.VITE_API_URL || '/admin';

interface ApiResponse<T> {
  data?: T;
  error?: string;
}

async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  try {
    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return { error: errorData.error || `HTTP ${response.status}` };
    }

    const data = await response.json();
    return { data };
  } catch (error) {
    return { error: error instanceof Error ? error.message : 'Unknown error' };
  }
}

// Events
export interface EventRecord {
  decision_id: string;
  created_at: string;
  route: string;
  route_class: string;
  verdict: string;
  outcome_code?: string;
  risk_score: number;
  reasons: string;
  ip: string;
  ip_hash: string;
  asn?: string;
  country?: string;
  device_id_hash?: string;
  email_hash?: string;
  email_domain?: string;
  phone_hash?: string;
  telemetry_validation_status?: string;
  telemetry_present: number;
  policy_version: string;
  latency_ms: number;
  user_agent?: string;
  // Explainability fields
  score_breakdown?: string;
  score_components?: string;
  matched_rules?: string;
  enforcement_path?: string;
  // Fingerprint fields
  fp_event_id?: string;
  fp_visitor_id?: string;
  fp_linked_id?: string;
  fp_request_id?: string;
  suspect_score?: number;
  suspect_score_version?: string;
  suspect_contributors?: string;
  smart_signals?: string;
  // Individual signal fields
  bot_result?: string;
  incognito?: number;
  vpn_result?: number;
  vpn_confidence?: string;
  tampering_result?: number;
  tampering_anomaly_score?: number;
  virtual_machine?: number;
  developer_tools?: number;
  proxy_result?: number;
  proxy_type?: string;
  datacenter_result?: number;
  high_activity_device?: number;
}

// Fingerprint Smart Signals
export interface SmartSignals {
  bot?: { result: 'good' | 'bad' | 'notDetected'; type?: string };
  incognito?: boolean;
  vpn?: {
    result: boolean;
    confidence: 'high' | 'medium' | 'low';
    originTimezone?: string;
    originCountry?: string;
    methods?: {
      timezoneMismatch?: boolean;
      publicVPN?: boolean;
      auxiliaryMobile?: boolean;
      osMismatch?: boolean;
      relay?: boolean;
    };
  };
  tampering?: {
    result: boolean;
    details?: { anomalyScore?: number; antiDetectBrowser?: boolean };
  };
  virtualMachine?: boolean;
  developerTools?: boolean;
  privacySettings?: boolean;
  proxy?: {
    result: boolean;
    confidence: 'high' | 'medium' | 'low';
    details?: { proxyType?: string };
  };
  ipInfo?: {
    asn?: { asn: string; name: string };
    datacenter?: { result: boolean; name?: string };
    geolocation?: {
      country?: { code: string; name: string };
      city?: { name: string };
      timezone?: string;
    };
  };
  velocity?: {
    distinctIp?: { '5m': number; '1h': number; '24h': number };
    distinctCountry?: { '5m': number; '1h': number; '24h': number };
    events?: { '5m': number; '1h': number; '24h': number };
  };
  highActivityDevice?: boolean;
}

// Suspect Score Contributors
export interface SuspectContributor {
  signal: string;
  weight: number;
  value: boolean | string | number;
}

// Rule Execution (for explainability)
export interface RuleExecution {
  execution_id: string;
  rule_id: string;
  rule_version?: string;
  rule_mode: 'monitor' | 'enforce';
  rule_action: 'allow' | 'challenge' | 'block';
  condition_expr?: string;
  condition_result: boolean;
  evaluated_inputs?: Record<string, unknown>;
  triggered: boolean;
  action_taken?: string;
  reason?: string;
  executed_at: string;
}

// Profile Types
export interface Profile {
  id: number;
  profile_id: string;
  tenant_id: string;
  fp_visitor_id: string;
  fp_linked_ids?: string[];
  ips_seen?: string[];
  asns_seen?: string[];
  countries_seen?: string[];
  current_risk_level?: 'low' | 'medium' | 'high' | 'critical';
  rolling_suspect_score_avg_24h?: number;
  max_suspect_score_7d?: number;
  risk_confidence?: number;
  velocity_24h_events: number;
  velocity_24h_distinct_ips: number;
  velocity_24h_distinct_countries: number;
  failed_login_rate_24h?: number;
  signup_attempts_24h: number;
  bot_bad_hits_7d: number;
  vpn_hits_7d: number;
  proxy_residential_hits_7d: number;
  tampering_hits_7d: number;
  devtools_hits_7d: number;
  high_activity_device_hits_7d: number;
  tor_hits_7d: number;
  labels?: string[];
  notes?: Array<{ ts: string; by: string; text: string }>;
  first_seen_at: string;
  last_seen_at: string;
  event_count_total: number;
  created_at: string;
  updated_at: string;
}

export interface ProfilesQuery {
  tenant_id?: string;
  risk_level?: string;
  min_score?: number;
  max_score?: number;
  label?: string;
  limit?: number;
  offset?: number;
}

export interface ProfilesResponse {
  profiles: Profile[];
  total: number;
}

export async function getProfiles(query: ProfilesQuery = {}): Promise<ApiResponse<ProfilesResponse>> {
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== '') {
      params.append(key, String(value));
    }
  });
  return apiRequest(`/profiles?${params}`);
}

export async function getProfile(visitorId: string): Promise<ApiResponse<{ profile: Profile }>> {
  return apiRequest(`/profiles/${visitorId}`);
}

export interface EventsQuery {
  page?: number;
  limit?: number;
  route_class?: string;
  verdict?: string;
  outcome_code?: string;
  telemetry_status?: string;
  country?: string;
  ip_hash?: string;
  device_id_hash?: string;
  email_hash?: string;
  phone_hash?: string;
  email_domain?: string;
  min_score?: number;
  max_score?: number;
  from?: string;
  to?: string;
}

export interface EventsResponse {
  events: EventRecord[];
  total: number;
  page: number;
  limit: number;
  has_more: boolean;
}

export async function getEvents(query: EventsQuery = {}): Promise<ApiResponse<EventsResponse>> {
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== '') {
      params.append(key, String(value));
    }
  });
  return apiRequest(`/events?${params}`);
}

export async function getEvent(decisionId: string): Promise<ApiResponse<{ event: EventRecord; labels: Label[] }>> {
  return apiRequest(`/events/${decisionId}`);
}

// Rulesets
export interface Ruleset {
  id: number;
  version: string;
  name: string;
  description?: string;
  is_active?: boolean;
  created_at?: string;
  created_by?: string;
  global: {
    enabled: boolean;
    default_verdict: string;
    emergency_mode: boolean;
    emergency_threshold_otp_5m: number;
  };
  routes: Record<string, {
    enabled: boolean;
    thresholds: {
      allow_below: number;
      challenge_below: number;
      block_above: number;
    };
    require_telemetry: boolean;
    step_up_action?: string;
  }>;
  scoring: {
    weights: Record<string, number>;
    telemetry_missing_multiplier: number;
    reputation_weight: number;
  };
  velocity: {
    device: Record<string, number>;
    ip: Record<string, number>;
    phone: Record<string, number>;
    email: Record<string, number>;
  };
  lists: {
    use_allowlist: boolean;
    use_denylist: boolean;
  };
  features: {
    telemetry_required: boolean;
    reputation_enabled: boolean;
    adaptive_thresholds: boolean;
    impossible_travel_check: boolean;
  };
}

export async function getRulesets(): Promise<ApiResponse<{ rulesets: Ruleset[] }>> {
  return apiRequest('/rulesets');
}

export async function getActiveRuleset(): Promise<ApiResponse<{ ruleset: Ruleset }>> {
  return apiRequest('/rulesets/active');
}

export async function createRuleset(ruleset: Omit<Ruleset, 'id'>): Promise<ApiResponse<{ id: number }>> {
  return apiRequest('/rulesets', {
    method: 'POST',
    body: JSON.stringify(ruleset),
  });
}

export async function activateRuleset(rulesetId: number): Promise<ApiResponse<{ message: string }>> {
  return apiRequest('/rulesets/activate', {
    method: 'POST',
    body: JSON.stringify({ ruleset_id: rulesetId }),
  });
}

// Lists
export interface ListEntry {
  id: number;
  list_type: 'ALLOW' | 'DENY';
  entry_type: string;
  value: string;
  notes?: string;
  expires_at?: string;
  is_active: boolean;
}

export async function getLists(type?: 'ALLOW' | 'DENY'): Promise<ApiResponse<{ entries: ListEntry[] }>> {
  const params = type ? `?type=${type}` : '';
  return apiRequest(`/lists${params}`);
}

export async function addListEntry(entry: Omit<ListEntry, 'id' | 'is_active'>): Promise<ApiResponse<{ id: number }>> {
  return apiRequest('/lists', {
    method: 'POST',
    body: JSON.stringify(entry),
  });
}

export async function removeListEntry(id: number): Promise<ApiResponse<{ message: string }>> {
  return apiRequest(`/lists/${id}`, {
    method: 'DELETE',
  });
}

export async function publishLists(): Promise<ApiResponse<{ message: string; allow_count: number; deny_count: number }>> {
  return apiRequest('/lists/publish', {
    method: 'POST',
  });
}

// Labels
export interface Label {
  id: number;
  decision_id: string;
  label: 'GOOD' | 'BAD' | 'SUSPICIOUS' | 'FALSE_POSITIVE';
  source: string;
  reason?: string;
  created_at: string;
  created_by: string;
}

export async function addLabel(data: {
  decision_id: string;
  label: string;
  source?: string;
  reason?: string;
}): Promise<ApiResponse<{ id: number }>> {
  return apiRequest('/labels', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// Simulate
export interface SimulateRequest {
  route_class: string;
  ip: string;
  user_agent: string;
  country?: string;
  asn?: string;
  device_id_hash?: string;
  email_hash?: string;
  phone_hash?: string;
}

export interface SimulateResponse {
  decision: {
    verdict: string;
    risk_score: number;
    reasons: string[];
  };
  signals: Record<string, unknown>;
  score_breakdown: { reason: string; score_contribution: number }[];
}

export async function simulate(request: SimulateRequest): Promise<ApiResponse<SimulateResponse>> {
  return apiRequest('/simulate', {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

// Metrics
export interface Metrics {
  total_events: number;
  by_verdict: Record<string, number>;
  by_route_class: Record<string, number>;
  by_country: Record<string, number>;
  avg_score: number;
  top_reasons: { reason: string; count: number }[];
}

export async function getMetrics(from?: string, to?: string): Promise<ApiResponse<Metrics>> {
  const params = new URLSearchParams();
  if (from) params.append('from', from);
  if (to) params.append('to', to);
  return apiRequest(`/metrics?${params}`);
}

// Audit
export interface AuditLog {
  id: number;
  actor: string;
  action: string;
  object_type: string;
  object_id: string;
  before_state?: string;
  after_state?: string;
  created_at: string;
}

export async function getAuditLogs(filters: {
  actor?: string;
  object_type?: string;
  from?: string;
  to?: string;
  limit?: number;
}): Promise<ApiResponse<{ logs: AuditLog[] }>> {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined) {
      params.append(key, String(value));
    }
  });
  return apiRequest(`/audit?${params}`);
}

// Reputation
export async function getReputation(entityType: string, entityHash: string): Promise<ApiResponse<{
  entity_type: string;
  entity_hash: string;
  state: {
    score: number;
    total_events: number;
    bad_events: number;
    good_events: number;
    last_seen_at: string;
  } | null;
}>> {
  return apiRequest(`/reputation?entity_type=${entityType}&entity_hash=${entityHash}`);
}

export async function adjustReputation(data: {
  entity_type: string;
  entity_hash: string;
  delta: number;
  reason?: string;
}): Promise<ApiResponse<{ new_score: number }>> {
  return apiRequest('/reputation/adjust', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}
